export interface IActionItem {
  text: string;
  owner: string;
  due: string;
}

export const safeParseJsonArray = <T,>(raw: string | null | undefined): T[] => {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed as T[];
    }
    return [];
  } catch {
    return [];
  }
};

export const resolveSuggestedCustomerNotes = (
  customerNotePrefill: string | null | undefined,
  summaryCustomerFacing: string | null | undefined,
): string => customerNotePrefill ?? summaryCustomerFacing ?? '';

export const customerNotesForServer = (notes: string): string =>
  notes.trim().length === 0 ? '' : notes;

const ACTION_ITEM_OWNERS: Record<string, string> = {
  tech: 'Technician',
  technician: 'Technician',
  customer: 'Customer',
};

const trimmedText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

export const parseActionItems = (raw: string | null | undefined): IActionItem[] =>
  safeParseJsonArray<unknown>(raw).flatMap(item => {
    if (typeof item === 'string') {
      const text = item.trim();
      return text ? [{ text, owner: '', due: '' }] : [];
    }
    if (item === null || typeof item !== 'object') {
      return [];
    }
    const fields = item as Record<string, unknown>;
    const text = trimmedText(fields.text) || trimmedText(fields.title) || trimmedText(fields.description);
    if (!text) {
      return [];
    }
    return [{
      text,
      owner: ACTION_ITEM_OWNERS[trimmedText(fields.owner).toLowerCase()] ?? '',
      due: trimmedText(fields.dueHint),
    }];
  });

export const OTHER_COMPLIANCE_FLAG = 'Other sensitive information discussed';

const COMPLIANCE_FLAG_PHRASES: Record<string, string> = {
  'credentials-shared': 'Credentials shared on the call',
  'payment-data-mentioned': 'Payment details discussed',
  'payment-details': 'Payment details discussed',
  'pii-collected': 'Personal information collected',
  'data-export-requested': 'Data export requested',
};

export const complianceFlagPhrases = (raw: string | null | undefined): string[] => {
  const phrases = safeParseJsonArray<unknown>(raw)
    .filter((flag): flag is string => typeof flag === 'string')
    .map(flag => flag.trim().replace(/[\s_]+/g, '-').toLowerCase())
    .filter(key => key.length > 0)
    .map(key => COMPLIANCE_FLAG_PHRASES[key] ?? OTHER_COMPLIANCE_FLAG);
  return [...new Set(phrases)];
};
